import { useEffect, useState } from "react";
import { X, Camera, ShieldCheck, Warning, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatBRL, formatApiError, fileUrl } from "../lib/api";
import { processAndUploadImage } from "../lib/imageUpload";

/**
 * V17.9 — Modal do Dono para SOLICITAR débito da caução.
 * Props: bookingId, caucaoAmount, onClose, onCreated
 */
export function OwnerCaucaoClaimModal({ bookingId, caucaoAmount, onClose, onCreated }) {
  const [amount, setAmount] = useState(String(caucaoAmount || ""));
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);   // {path, thumb?}
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadOne = async (file) => {
    setUploading(true);
    try {
      const r = await processAndUploadImage(file);
      setFiles((prev) => [...prev, { path: r.path }]);
    } catch (e) {
      toast.error(e.message || "Falha no upload");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.warning("Informe o valor a debitar.");
    if (amt > caucaoAmount) return toast.warning(`Valor não pode exceder a caução retida (${formatBRL(caucaoAmount)}).`);
    if ((reason || "").trim().length < 20) return toast.warning("Descreva o motivo com pelo menos 20 caracteres.");
    if (files.length === 0) return toast.warning("Envie pelo menos 1 foto de evidência.");
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${bookingId}/caucao-claim`, {
        amount: amt, reason: reason.trim(),
        evidence_files: files.map((f) => f.path),
      });
      toast.success("Solicitação enviada. Locatário tem 48h para responder.");
      onCreated && onCreated(r.data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao criar solicitação");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end md:items-center md:justify-center p-0 md:p-4" onClick={(e) => !busy && e.target === e.currentTarget && onClose()} data-testid="owner-caucao-claim-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 text-amber-700">
            <ShieldCheck size={22} weight="fill" />
            <h3 className="font-heading font-black text-lg">Solicitar débito da caução</h3>
          </div>
          {!busy && (
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Fechar">
              <X size={20} weight="bold" />
            </button>
          )}
        </div>

        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2.5 leading-relaxed">
          <b>Como funciona:</b> o locatário tem <b>48h</b> para aceitar (débito imediato) ou contestar
          (defesa vai ao suporte AlugaJá). <b>Sem resposta em 48h → débito automático a seu favor.</b>
        </div>

        <label className="block">
          <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">
            Valor a debitar (R$) <span className="text-red-600">*</span>
          </span>
          <input type="number" min="0.01" step="0.01" max={caucaoAmount}
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="aj-input mt-1" data-testid="caucao-claim-amount" />
          <div className="text-[10px] text-slate-500 mt-0.5">Caução retida: <b>{formatBRL(caucaoAmount)}</b></div>
        </label>

        <label className="block">
          <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">
            Motivo da cobrança <span className="text-red-600">*</span>
          </span>
          <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Ex.: item devolvido com risco profundo na lateral direita, precisa retoque na oficina..."
            className="aj-input mt-1 resize-none" data-testid="caucao-claim-reason" />
          <div className="text-[10px] text-slate-500 mt-0.5">Mín. 20 caracteres · {reason.length}/500</div>
        </label>

        <div>
          <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">
            Fotos de evidência <span className="text-red-600">*</span>
          </span>
          <div className="grid grid-cols-3 gap-2 mt-1.5">
            {files.map((f, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden border-2 border-slate-200" data-testid={`caucao-claim-evidence-${i}`}>
                <img src={fileUrl(f.path)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                  aria-label="Remover">
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            ))}
            {files.length < 6 && (
              <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:bg-slate-50" data-testid="caucao-claim-add-photo">
                <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadOne(e.target.files[0])} />
                {uploading ? (
                  <span className="text-[10px] text-slate-500">Enviando...</span>
                ) : (
                  <div className="flex flex-col items-center gap-0.5 text-slate-500">
                    <Camera size={20} weight="bold" />
                    <span className="text-[10px] font-bold">Adicionar</span>
                  </div>
                )}
              </label>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2">
          <button onClick={onClose} disabled={busy} className="btn-ghost" data-testid="caucao-claim-cancel">Cancelar</button>
          <button onClick={submit} disabled={busy || uploading || files.length === 0 || reason.trim().length < 20}
            className="btn-primary bg-amber-600 hover:bg-amber-700 disabled:opacity-50" data-testid="caucao-claim-submit">
            {busy ? "Enviando..." : "Solicitar débito"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * V17.9 — Banner + ações para o LOCATÁRIO responder a claim (aceitar / contestar).
 * Props: bookingId, claim (doc do caucao_claim), onDone
 */
export function RenterCaucaoClaimBanner({ bookingId, claim, onDone }) {
  const [showContest, setShowContest] = useState(false);
  const [defense, setDefense] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [remaining, setRemaining] = useState("");

  useEffect(() => {
    if (!claim?.expires_at) return;
    const tick = () => {
      const now = Date.now();
      const exp = new Date(claim.expires_at).getTime();
      const diff = Math.max(0, exp - now);
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setRemaining(diff <= 0 ? "expirado" : (h >= 1 ? `${h}h ${m}m` : `${m}min`));
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [claim?.expires_at]);

  const accept = async () => {
    if (!confirm(`Aceitar o débito de ${formatBRL(claim.amount)} da sua caução?`)) return;
    setBusy(true);
    try {
      await api.post(`/caucao-claims/${claim.id}/accept`);
      toast.success("Débito confirmado.");
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao aceitar");
    } finally { setBusy(false); }
  };

  const uploadOne = async (file) => {
    setUploading(true);
    try {
      const r = await processAndUploadImage(file);
      setFiles((prev) => [...prev, { path: r.path }]);
    } catch (e) {
      toast.error(e.message || "Falha no upload");
    } finally { setUploading(false); }
  };

  const submitContest = async () => {
    if (defense.trim().length < 20) return toast.warning("Descreva sua defesa com pelo menos 20 caracteres.");
    if (files.length === 0) return toast.warning("Envie pelo menos 1 foto/evidência.");
    setBusy(true);
    try {
      await api.post(`/caucao-claims/${claim.id}/contest`, {
        defense: defense.trim(), evidence_files: files.map((f) => f.path),
      });
      toast.success("Contestação enviada ao suporte AlugaJá.");
      setShowContest(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao contestar");
    } finally { setBusy(false); }
  };

  if (!claim) return null;

  return (
    <>
      <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3" data-testid="renter-caucao-claim-banner">
        <div className="text-sm text-red-900 font-bold flex items-center gap-1.5">
          <Warning size={18} weight="fill" /> Solicitação de débito de caução
        </div>
        <p className="text-xs text-red-900 mt-1">
          O dono solicitou débito de <b>{formatBRL(claim.amount)}</b> da caução retida.
          <br /><b>Motivo:</b> {claim.reason}
        </p>
        {claim.evidence_files?.length > 0 && (
          <div className="grid grid-cols-4 gap-1.5 mt-2" data-testid="renter-claim-evidence-preview">
            {claim.evidence_files.slice(0, 4).map((p, i) => (
              <a key={i} href={fileUrl(p)} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg overflow-hidden border border-red-200 block">
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        )}
        <div className="mt-2 text-[11px] font-bold text-red-800 bg-red-100 rounded-md px-2 py-1.5">
          ⏰ Restam <span data-testid="renter-claim-remaining">{remaining}</span> para responder.
          Sem manifestação, o valor será debitado automaticamente.
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button onClick={accept} disabled={busy} className="btn-primary bg-red-600 hover:bg-red-700 text-sm py-2 disabled:opacity-50" data-testid="renter-claim-accept">
            Aceitar débito
          </button>
          <button onClick={() => setShowContest(true)} disabled={busy} className="btn-ghost text-sm py-2 border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50" data-testid="renter-claim-contest">
            Contestar
          </button>
        </div>
      </div>

      {showContest && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-end md:items-center md:justify-center p-0 md:p-4" onClick={(e) => !busy && e.target === e.currentTarget && setShowContest(false)} data-testid="renter-contest-modal">
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 text-red-700">
                <Warning size={22} weight="fill" />
                <h3 className="font-heading font-black text-lg">Contestar débito</h3>
              </div>
              {!busy && (
                <button onClick={() => setShowContest(false)} className="text-slate-400 hover:text-slate-700"><X size={20} weight="bold" /></button>
              )}
            </div>
            <div className="text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded-lg p-2.5">
              Explique sua defesa e envie fotos. O suporte AlugaJá vai revisar e decidir.
            </div>
            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Sua defesa <span className="text-red-600">*</span></span>
              <textarea rows={4} value={defense} onChange={(e) => setDefense(e.target.value)}
                placeholder="Ex.: o risco descrito já existia antes da locação. Foto abaixo mostra o item ao retirar..."
                className="aj-input mt-1 resize-none" data-testid="renter-contest-defense" />
              <div className="text-[10px] text-slate-500 mt-0.5">Mín. 20 caracteres</div>
            </label>
            <div>
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Fotos de evidência <span className="text-red-600">*</span></span>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {files.map((f, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border-2 border-slate-200" data-testid={`renter-contest-evidence-${i}`}>
                    <img src={fileUrl(f.path)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"><Trash size={12} weight="bold" /></button>
                  </div>
                ))}
                {files.length < 6 && (
                  <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:bg-slate-50" data-testid="renter-contest-add-photo">
                    <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadOne(e.target.files[0])} />
                    {uploading ? <span className="text-[10px] text-slate-500">Enviando...</span> :
                      <div className="flex flex-col items-center gap-0.5 text-slate-500"><Camera size={20} weight="bold" /><span className="text-[10px] font-bold">Adicionar</span></div>}
                  </label>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button onClick={() => setShowContest(false)} disabled={busy} className="btn-ghost" data-testid="renter-contest-cancel">Cancelar</button>
              <button onClick={submitContest} disabled={busy || uploading || files.length === 0 || defense.trim().length < 20}
                className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50" data-testid="renter-contest-submit">
                {busy ? "Enviando..." : "Enviar contestação"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
